import React, { useState, useRef, useEffect } from 'react';

interface DatePickerProps {
  selected: Date | null | any;  // Kibővítjük a típust, hogy Timestamp-et is elfogadjon
  onChange: (date: Date | null) => void;
  className?: string;
  placeholderText?: string;
}

export const DatePicker: React.FC<DatePickerProps> = ({ selected, onChange, className, placeholderText }) => {
  const inputRef = useRef<any>(null);
  const [displayValue, setDisplayValue] = useState<string>('');
  const [focused, setFocused] = useState(false);
  
  // Format date as YYYY-MM-DD for the input value
  const formatDate = (dateInput: any): string => {
    if (!dateInput) return '';
    
    try {
      // Ha Timestamp objektum (Firebase), akkor konvertáljuk Date-té
      if (dateInput && typeof dateInput === 'object' && 'toDate' in dateInput) {
        try {
          const date = dateInput.toDate();
          if (isNaN(date.getTime())) return '';
          return `${date.getFullYear()}.${String(date.getMonth() + 1).padStart(2, '0')}.${String(date.getDate()).padStart(2, '0')}`;
        } catch (e) {
          console.error('Error converting Timestamp to Date:', e);
          return '';
        }
      }
      
      // Ha string formátumú dátum pont-elválasztóval (yyyy.MM.dd.)
      if (typeof dateInput === 'string' && dateInput.match(/^\d{4}\.\d{2}\.\d{2}\.?$/)) {
        const [year, month, day] = dateInput.split('.').filter(Boolean);
        if (year && month && day) {
          return `${year}.${month}.${day}`;
        }
      }
      
      // Ha string, akkor próbáljuk meg konvertálni dátummá
      if (typeof dateInput === 'string') {
        const dateParts = dateInput.split(/[-T.]/);
        if (dateParts.length >= 3) {
          const year = parseInt(dateParts[0]);
          const month = parseInt(dateParts[1]);
          const day = parseInt(dateParts[2]);
          
          if (!isNaN(year) && !isNaN(month) && !isNaN(day)) {
            return `${year}.${String(month).padStart(2, '0')}.${String(day).padStart(2, '0')}`;
          }
        }
      }
      
      // Ha Date objektum
      if (dateInput instanceof Date) {
        if (isNaN(dateInput.getTime())) return '';
        return `${dateInput.getFullYear()}.${String(dateInput.getMonth() + 1).padStart(2, '0')}.${String(dateInput.getDate()).padStart(2, '0')}`;
      }
      
      // Egyéb esetekben próbáljuk meg Date objektummá konvertálni
      const date = new Date(dateInput);
      if (isNaN(date.getTime())) return '';
      return `${date.getFullYear()}.${String(date.getMonth() + 1).padStart(2, '0')}.${String(date.getDate()).padStart(2, '0')}`;
      
    } catch (error) {
      console.error('Invalid date in DatePicker:', error, dateInput);
      return '';
    }
  };

  // Update display value when selected date changes
  useEffect(() => {
    setDisplayValue(formatDate(selected));
  }, [selected]);

  const applyMask = (value: string): string => {
    const digits = value.replace(/\D/g, '').slice(0, 8);
    let result = '';
    if (digits.length >= 1) result = digits.slice(0, 4);
    if (digits.length >= 5) result += '.' + digits.slice(4, 6);
    if (digits.length >= 7) result += '.' + digits.slice(6, 8);
    return result;
  };

  const handleDateChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    const masked = applyMask(value);
    setDisplayValue(masked);

    if (masked && masked.match(/^\d{4}\.\d{2}\.\d{2}$/)) {
      try {
        const [year, month, day] = masked.split('.');
        const date = new Date(parseInt(year), parseInt(month) - 1, parseInt(day));

        if (isNaN(date.getTime())) {
          console.error('Invalid date entered:', masked);
          onChange(null);
          return;
        }

        const utcDate = new Date(Date.UTC(date.getFullYear(), date.getMonth(), date.getDate()));
        onChange(utcDate);
      } catch (error) {
        console.error('Error processing date:', error);
        onChange(null);
      }
    } else if (!masked) {
      onChange(null);
    }
  };

  // Combine the passed className with our custom overrides
  const combinedClassName = `${className || ''} date-input-custom`.trim();

  return (
    <input
      type="text"
      inputMode="numeric"
      value={displayValue}
      onChange={handleDateChange}
      onFocus={() => setFocused(true)}
      onBlur={() => setFocused(false)}
      className={combinedClassName}
      placeholder={placeholderText || "ÉÉÉÉ.HH.NN"}
      maxLength={10}
      ref={inputRef}
    />
  );
}; 